import { useEffect, useRef, useState } from 'react';
import { CameraIcon, PlusIcon, XIcon } from 'lucide-react';
import {
  CANONICAL_SPECIALISATIONS,
  MELDING_VOORKEUREN_STANDAARD,
  type MeldingVoorkeuren,
  type UpdateProfileInput,
  specialisationLabel,
} from '@kennisbank/shared';
import { Layout } from '../components/Layout.js';
import { useAuth } from '../auth/AuthProvider.js';
import { ApiError, apiGet, apiPost, apiPut } from '../api/client.js';
import { initials } from '../lib/format.js';
import { safeImageSrc } from '../lib/url.js';
import { toUploadable } from '../lib/image.js';
import { TeamleiderKiezer } from '../components/declaraties/TeamleiderKiezer.js';
import { useTenantBrand } from '../lib/tenant-brand.js';

interface ProfileData {
  photoUrl?: string;
  weergavenaam?: string;
  specialisaties?: string[];
  regio?: string;
  locatie?: string;
  biografie?: string;
  opleidingErvaring?: string;
  pohGgzSinds?: string;
  functietitel?: string;
  profielType?: 'poh_ggz' | 'kantoor';
  telefoon?: string;
  rekeningnummer?: string;
  woonplaats?: string;
  woonplaatsZichtbaar?: boolean;
  lidnummerLvpoh?: string;
  lidnummerSkj?: string;
  profileCompletedAt?: string;
  meldingen?: MeldingVoorkeuren;
}

export function ProfilePage(): JSX.Element {
  const { user } = useAuth();
  const { collegas } = useTenantBrand();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  // photoUrl = signed display URL (shown); photoObject = GCS object path to
  // persist. photoObject is only set when a NEW photo is uploaded this session;
  // left undefined on save it is dropped, so the backend preserves the existing
  // photo (Firestore ignoreUndefinedProperties + merge). The bucket is private.
  const [photoUrl, setPhotoUrl] = useState<string | undefined>(undefined);
  const [photoObject, setPhotoObject] = useState<string | undefined>(undefined);
  const [weergavenaam, setWeergavenaam] = useState('');
  const [meldingen, setMeldingen] = useState<MeldingVoorkeuren>(MELDING_VOORKEUREN_STANDAARD);
  const [specialisaties, setSpecialisaties] = useState<string[]>([]);
  const [regio, setRegio] = useState('');
  const [locatie, setLocatie] = useState('');
  const [biografie, setBiografie] = useState('');
  const [opleidingErvaring, setOpleidingErvaring] = useState('');
  const [pohGgzSinds, setPohGgzSinds] = useState('');
  const [functietitel, setFunctietitel] = useState('');
  const [profielType, setProfielType] = useState<'poh_ggz' | 'kantoor' | ''>('');
  const [telefoon, setTelefoon] = useState('');
  const [rekeningnummer, setRekeningnummer] = useState('');
  const [woonplaats, setWoonplaats] = useState('');
  const [woonplaatsZichtbaar, setWoonplaatsZichtbaar] = useState(false);
  const [lidnummerLvpoh, setLidnummerLvpoh] = useState('');
  const [lidnummerSkj, setLidnummerSkj] = useState('');
  const [newSpec, setNewSpec] = useState('');
  const [specError, setSpecError] = useState('');
  // Aanvraag-status voor een specialisatie die (nog) niet in de curated lijst
  // staat. De aanvraag loopt via het bestaande wijzigingsverzoek-kanaal.
  const [specRequest, setSpecRequest] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saved, setSaved] = useState(false);

  // Kantoorpersoneel (Entra-groep Kantoorpersoneel) is geen POH-GGZ: geen
  // specialisaties, praktijk, opleiding of lidnummers, wel een functietitel.
  // Admins kiezen zelf (ze zijn standaard kantoor); anderen volgen de groep.
  const isAdmin = user?.role === 'suppohrt_admin';
  const isKantoor = profielType ? profielType === 'kantoor' : user?.isHeadOffice === true;
  // Een term die al in de lijst staat hoef je niet aan te vragen.
  const newSpecIsCanonical = CANONICAL_SPECIALISATIONS.some(
    (c) => c.toLowerCase() === newSpec.trim().toLowerCase(),
  );

  useEffect(() => {
    if (!user) return;
    void apiGet<{ user: ProfileData }>('/api/v1/profile/me')
      .then((d) => {
        // Saneren bij het schrijven: state bevat nooit een onveilige URL.
        setPhotoUrl(safeImageSrc(d.user.photoUrl) || undefined);
        setWeergavenaam(d.user.weergavenaam ?? '');
        setMeldingen(d.user.meldingen ?? MELDING_VOORKEUREN_STANDAARD);
        setSpecialisaties(d.user.specialisaties ?? []);
        setRegio(d.user.regio ?? '');
        setLocatie(d.user.locatie ?? '');
        setBiografie(d.user.biografie ?? '');
        setOpleidingErvaring(d.user.opleidingErvaring ?? '');
        setPohGgzSinds(d.user.pohGgzSinds ?? '');
        setFunctietitel(d.user.functietitel ?? '');
        setProfielType(d.user.profielType ?? '');
        setTelefoon(d.user.telefoon ?? '');
        setRekeningnummer(d.user.rekeningnummer ?? '');
        setWoonplaats(d.user.woonplaats ?? '');
        setWoonplaatsZichtbaar(d.user.woonplaatsZichtbaar === true);
        setLidnummerLvpoh(d.user.lidnummerLvpoh ?? '');
        setLidnummerSkj(d.user.lidnummerSkj ?? '');
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [user]);

  // Zelfde grens als backend/src/routes/profile.ts — samen houden.
  const MAX_PHOTO_MB = 10;
  const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

  // Zod-fouten komen binnen als generieke "validation_error" met de echte
  // (Nederlandse) melding in detail.fieldErrors; HttpError-meldingen zitten
  // direct in message. Alles daarbuiten valt terug op de generieke tekst.
  function describeUploadError(err: unknown): string {
    if (err instanceof ApiError) {
      const fieldErrors = (err.detail as { fieldErrors?: Record<string, string[]> } | undefined)
        ?.fieldErrors;
      const first = fieldErrors && Object.values(fieldErrors).flat()[0];
      if (first) return first;
      if (err.code !== 'validation_error' && !err.code.startsWith('http_')) return err.message;
    }
    return 'Foto uploaden mislukt. Probeer opnieuw.';
  }

  async function handlePhotoSelect(input: File): Promise<void> {
    setUploadError('');
    setUploading(true);
    try {
      let file: File;
      try {
        // 512 px: de grootste weergave is de profielkop; overal elders is het een rondje.
        file = await toUploadable(input, 512);
      } catch {
        setUploadError(
          'Kon de HEIC-foto niet omzetten. Sla de foto op als JPEG en probeer opnieuw.',
        );
        return;
      }
      if (!PHOTO_TYPES.includes(file.type)) {
        setUploadError(
          'Dit bestandstype wordt niet ondersteund. Kies een JPEG-, PNG-, WebP- of HEIC-afbeelding.',
        );
        return;
      }
      if (file.size > MAX_PHOTO_MB * 1024 * 1024) {
        setUploadError(
          `Deze foto is ${(file.size / (1024 * 1024)).toFixed(1)} MB; maximaal ${MAX_PHOTO_MB} MB toegestaan. Verklein de foto en probeer opnieuw.`,
        );
        return;
      }
      const {
        uploadUrl,
        photoObject: newObject,
        photoUrl: newUrl,
      } = await apiPost<{
        uploadUrl: string;
        photoObject: string;
        photoUrl?: string;
      }>('/api/v1/profile/photo-upload-url', {
        mimeType: file.type,
        sizeBytes: file.size,
      });
      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'content-type': file.type },
        body: file,
      });
      if (!putRes.ok) throw new Error(`upload failed: ${putRes.status}`);
      setPhotoObject(newObject);
      setPhotoUrl(safeImageSrc(newUrl) || undefined);
    } catch (err) {
      setUploadError(describeUploadError(err));
    } finally {
      setUploading(false);
    }
  }

  function addSpecialisatie(): void {
    const v = newSpec.trim();
    if (!v || specialisaties.length >= 10) return;
    // Match case-insensitively but store the canonical casing from the set.
    const canonical = CANONICAL_SPECIALISATIONS.find((c) => c.toLowerCase() === v.toLowerCase());
    if (!canonical) {
      setSpecError(`"${v}" staat niet in de lijst.`);
      setSpecRequest('idle');
      return;
    }
    setSpecError('');
    if (specialisaties.includes(canonical)) {
      setNewSpec('');
      return;
    }
    setSpecialisaties([...specialisaties, canonical]);
    setNewSpec('');
  }

  function removeSpecialisatie(s: string): void {
    setSpecialisaties(specialisaties.filter((x) => x !== s));
  }

  // Verstuur een verzoek om een niet-gecureerde specialisatie toe te voegen.
  // Hergebruikt het wijzigingsverzoek-kanaal (type 'overig') dat in de
  // Beheer-wachtrij belandt; de curated enum blijft een bewuste code-change.
  async function requestSpecialisatie(): Promise<void> {
    const v = newSpec.trim();
    if (!v || specRequest === 'sending') return;
    setSpecRequest('sending');
    try {
      await apiPost('/api/v1/change-requests', {
        type: 'overig',
        title: `Nieuwe specialisatie aangevraagd: ${v}`.slice(0, 200),
        description:
          `${user?.displayName ?? 'Een medewerker'} vraagt of de specialisatie ` +
          `"${v}" aan de keuzelijst kan worden toegevoegd (aangevraagd vanuit het profiel).`,
      });
      setSpecRequest('sent');
      setSpecError('');
    } catch {
      setSpecError('Aanvraag versturen mislukt. Probeer het later opnieuw.');
      setSpecRequest('idle');
    }
  }

  async function handleSave(): Promise<void> {
    setSaving(true);
    setSaveError('');
    const input: UpdateProfileInput = {
      // Only send photoObject when a new photo was uploaded this session;
      // undefined leaves the existing photo untouched (backend merge).
      photoObject: photoObject ?? undefined,
      // Altijd meesturen (ook leeg): een lege string wist de eigen naam terug
      // naar de Entra-naam — `undefined` zou door de merge worden genegeerd.
      weergavenaam: weergavenaam.trim(),
      meldingen,
      // Altijd meesturen, ook leeg: de backend vertaalt leeg naar "wissen".
      // Met `undefined` bleef een weggehaalde waarde stilzwijgend staan.
      specialisaties,
      regio: regio.trim(),
      locatie: locatie.trim(),
      biografie: biografie.trim(),
      opleidingErvaring: opleidingErvaring.trim(),
      pohGgzSinds,
      // Altijd meesturen: leeg wist de titel (merge negeert undefined). Wie
      // uit de kantoorgroep valt ziet het veld niet meer en wist zo de oude
      // titel in plaats van een 400 te krijgen.
      functietitel: isKantoor ? functietitel.trim() : '',
      profielType: isAdmin && profielType ? profielType : undefined,
      telefoon: telefoon.trim(),
      // null = wissen; een weggelaten veld zou het oude IBAN laten staan.
      rekeningnummer: rekeningnummer.trim() || null,
      woonplaats: woonplaats.trim(),
      woonplaatsZichtbaar,
      lidnummerLvpoh: lidnummerLvpoh.trim(),
      lidnummerSkj: lidnummerSkj.trim(),
    };
    try {
      await apiPut('/api/v1/profile', input);
      // Laat de Layout (aanvul-banner + header-avatar) direct bijwerken.
      window.dispatchEvent(new Event('profile-saved'));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setSaveError('Opslaan mislukt. Probeer opnieuw.');
    } finally {
      setSaving(false);
    }
  }

  if (!user) return <></>;

  return (
    <Layout>
      <div className="mx-auto max-w-2xl px-5 py-8">
        <div className="mb-6">
          <h1 className="text-[20px] font-bold text-ink-900">Mijn profiel</h1>
          <p className="mt-1 text-[13px] text-ink-900/55">
            Dit profiel is zichtbaar in het Smoelenboek voor {collegas}.
          </p>
        </div>

        {loading ? (
          <div className="py-12 text-center text-[13px] text-ink-900/40">Laden…</div>
        ) : loadError ? (
          <div className="py-12 text-center text-[13px] text-error">
            Kon je profiel niet laden. Ververs de pagina om het opnieuw te proberen.
          </div>
        ) : (
          <div className="space-y-6">
            {/* Foto */}
            <div className="rounded-2xl border border-brand-100 bg-white p-6">
              <h2 className="mb-4 text-[13px] font-semibold uppercase tracking-wider text-ink-900/50">
                Profielfoto
              </h2>
              <div className="flex items-center gap-5">
                <div className="relative">
                  {photoUrl ? (
                    <img
                      src={safeImageSrc(photoUrl)}
                      alt={user.displayName}
                      className="h-20 w-20 rounded-full object-cover ring-2 ring-brand-100"
                    />
                  ) : (
                    <div className="flex h-20 w-20 items-center justify-center rounded-full bg-brand-100 ring-2 ring-brand-200">
                      <span className="text-[22px] font-bold text-brand-700">
                        {initials(user.displayName)}
                      </span>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full bg-brand-600 text-white shadow hover:bg-brand-700 disabled:opacity-50"
                    aria-label="Foto wijzigen"
                  >
                    <CameraIcon size={13} />
                  </button>
                </div>
                <div className="min-w-0">
                  <p className="text-[14px] font-semibold text-ink-900">{user.displayName}</p>
                  <p className="text-[12px] text-ink-900/50">{user.email}</p>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="mt-2 text-[12px] font-medium text-brand-700 hover:underline disabled:opacity-50"
                  >
                    {uploading ? 'Uploaden…' : 'Foto uploaden'}
                  </button>
                  {uploadError && <p className="mt-1 text-[12px] text-error">{uploadError}</p>}
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void handlePhotoSelect(file);
                }}
              />

              <div className="mt-5 border-t border-brand-100 pt-5">
                <label className="mb-1.5 block text-[12px] font-medium text-ink-900/70">
                  Weergavenaam
                </label>
                <p className="mb-2 text-[12px] text-ink-900/55">
                  Optioneel. Laat je dit leeg, dan tonen we je Microsoft-naam ({user.displayName})
                  in het Smoelenboek.
                </p>
                <input
                  type="text"
                  value={weergavenaam}
                  onChange={(e) => setWeergavenaam(e.target.value)}
                  maxLength={200}
                  placeholder={user.displayName}
                  className="w-full max-w-sm rounded-lg border border-brand-100 bg-brand-50/50 px-3 py-2 text-[13px] text-ink-900 placeholder:text-ink-900/35 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
                />
              </div>
              {isAdmin && (
                <div className="mt-5 border-t border-brand-100 pt-5">
                  <label className="mb-1.5 block text-[12px] font-medium text-ink-900/70">
                    Profieltype
                  </label>
                  <p className="mb-2 text-[12px] text-ink-900/55">
                    Als admin kies je zelf hoe je profiel in het Smoelenboek staat.
                  </p>
                  <div className="flex gap-4 text-[13px] text-ink-900">
                    {(
                      [
                        ['poh_ggz', 'POH-GGZ'],
                        ['kantoor', 'Kantoorfunctie'],
                      ] as const
                    ).map(([v, label]) => (
                      <label key={v} className="flex items-center gap-1.5">
                        <input
                          type="radio"
                          name="profieltype"
                          value={v}
                          checked={isKantoor === (v === 'kantoor')}
                          onChange={() => setProfielType(v)}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {isKantoor && (
                <div className="mt-5 border-t border-brand-100 pt-5">
                  <label className="mb-1.5 block text-[12px] font-medium text-ink-900/70">
                    Functietitel
                  </label>
                  <p className="mb-2 text-[12px] text-ink-900/55">
                    Je bent kantoorpersoneel. Deze titel staat in het Smoelenboek bij je naam.
                  </p>
                  <input
                    type="text"
                    value={functietitel}
                    onChange={(e) => setFunctietitel(e.target.value)}
                    maxLength={100}
                    placeholder="bijv. Officemanager"
                    className="w-full max-w-sm rounded-lg border border-brand-100 bg-brand-50/50 px-3 py-2 text-[13px] text-ink-900 placeholder:text-ink-900/35 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
                  />
                </div>
              )}
            </div>

            {!isKantoor && (
              <>
                {/* Expertise / specialisaties */}
                <div className="rounded-2xl border border-brand-100 bg-white p-6">
                  <h2 className="mb-1 text-[13px] font-semibold uppercase tracking-wider text-ink-900/50">
                    Expertise / specialisaties
                  </h2>
                  <p className="mb-4 text-[12px] text-ink-900/55">
                    In welke thema&apos;s en methodieken voel je je het meest bedreven? Waar zou een
                    collega met je over kunnen sparren? Kies uit de lijst.
                  </p>
                  <div className="mb-3 flex flex-wrap gap-2">
                    {specialisaties.map((s) => (
                      <span
                        key={s}
                        className="flex items-center gap-1 rounded-full bg-brand-50 px-3 py-1 text-[12px] font-medium text-brand-700"
                      >
                        {specialisationLabel(s)}
                        <button
                          type="button"
                          onClick={() => removeSpecialisatie(s)}
                          aria-label={`Verwijder ${s}`}
                          className="text-brand-500 hover:text-brand-800"
                        >
                          <XIcon size={11} />
                        </button>
                      </span>
                    ))}
                    {specialisaties.length === 0 && (
                      <p className="text-[12px] text-ink-900/40">
                        Nog geen specialisaties toegevoegd.
                      </p>
                    )}
                  </div>
                  {specialisaties.length < 10 && (
                    <>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          list="canonical-specialisaties"
                          value={newSpec}
                          onChange={(e) => {
                            setNewSpec(e.target.value);
                            if (specError) setSpecError('');
                            if (specRequest !== 'idle') setSpecRequest('idle');
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addSpecialisatie();
                            }
                          }}
                          placeholder="Kies een specialisatie…"
                          className="flex-1 rounded-lg border border-brand-100 bg-brand-50/50 px-3 py-2 text-[13px] text-ink-900 placeholder:text-ink-900/35 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
                        />
                        <datalist id="canonical-specialisaties">
                          {CANONICAL_SPECIALISATIONS.filter((c) => !specialisaties.includes(c)).map(
                            (c) => (
                              <option key={c} value={c} />
                            ),
                          )}
                        </datalist>
                        <button
                          type="button"
                          onClick={addSpecialisatie}
                          disabled={!newSpec.trim()}
                          className="flex items-center gap-1 rounded-lg bg-brand-50 px-3 py-2 text-[13px] font-medium text-brand-700 hover:bg-brand-100 disabled:opacity-40"
                        >
                          <PlusIcon size={14} />
                          Voeg toe
                        </button>
                      </div>
                      {specError && <p className="mt-2 text-[12px] text-error">{specError}</p>}
                      <div className="mt-2 flex flex-wrap items-center gap-2 text-[12px]">
                        <span className="text-ink-900/55">
                          Staat je specialisatie er niet bij? Typ hem hierboven en
                        </span>
                        {specRequest === 'sent' ? (
                          <span className="font-medium text-brand-700">
                            Aanvraag verstuurd naar de beheerder.
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => void requestSpecialisatie()}
                            disabled={
                              specRequest === 'sending' || !newSpec.trim() || newSpecIsCanonical
                            }
                            className="font-medium text-brand-700 underline hover:text-brand-800 disabled:opacity-50"
                          >
                            {specRequest === 'sending'
                              ? 'Versturen…'
                              : 'vraag hem aan bij de beheerder'}
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* Locatie */}
                <div className="rounded-2xl border border-brand-100 bg-white p-6">
                  <h2 className="mb-4 text-[13px] font-semibold uppercase tracking-wider text-ink-900/50">
                    Locatie
                  </h2>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-1.5 block text-[12px] font-medium text-ink-900/70">
                        Regio
                      </label>
                      <input
                        type="text"
                        value={regio}
                        onChange={(e) => setRegio(e.target.value)}
                        placeholder="bijv. Amsterdam"
                        className="w-full rounded-lg border border-brand-100 bg-brand-50/50 px-3 py-2 text-[13px] text-ink-900 placeholder:text-ink-900/35 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
                      />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-[12px] font-medium text-ink-900/70">
                        Huisartsenpraktijk(en)
                      </label>
                      <input
                        type="text"
                        value={locatie}
                        onChange={(e) => setLocatie(e.target.value)}
                        placeholder="bijv. Huisartsenpraktijk De Brug"
                        className="w-full rounded-lg border border-brand-100 bg-brand-50/50 px-3 py-2 text-[13px] text-ink-900 placeholder:text-ink-900/35 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
                      />
                    </div>
                  </div>
                </div>

                {/* Opleiding, ervaring & POH-GGZ sinds */}
                <div className="rounded-2xl border border-brand-100 bg-white p-6">
                  <h2 className="mb-4 text-[13px] font-semibold uppercase tracking-wider text-ink-900/50">
                    Opleiding &amp; ervaring
                  </h2>
                  <textarea
                    value={opleidingErvaring}
                    onChange={(e) => setOpleidingErvaring(e.target.value)}
                    rows={3}
                    maxLength={1000}
                    placeholder="Opleiding en eerdere werkvelden."
                    className="w-full resize-y rounded-lg border border-brand-100 bg-brand-50/50 px-3 py-2 text-[13px] text-ink-900 placeholder:text-ink-900/35 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
                  />
                  <div className="mt-4">
                    <label className="mb-1.5 block text-[12px] font-medium text-ink-900/70">
                      POH-GGZ sinds
                    </label>
                    <input
                      type="date"
                      value={pohGgzSinds}
                      onChange={(e) => setPohGgzSinds(e.target.value)}
                      className="rounded-lg border border-brand-100 bg-brand-50/50 px-3 py-2 text-[13px] text-ink-900 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Over mij */}
            <div className="rounded-2xl border border-brand-100 bg-white p-6">
              <h2 className="mb-1 text-[13px] font-semibold uppercase tracking-wider text-ink-900/50">
                Over mij
              </h2>
              <p className="mb-4 text-[12px] text-ink-900/55">
                Deel in 5 à 6 zinnen iets over wie je bent: hobby&apos;s, passies, gezin, interesses
                buiten het werk, drijfveren.
              </p>
              <textarea
                value={biografie}
                onChange={(e) => setBiografie(e.target.value)}
                rows={5}
                maxLength={1000}
                placeholder="Vertel iets over jezelf…"
                className="w-full resize-y rounded-lg border border-brand-100 bg-brand-50/50 px-3 py-2 text-[13px] text-ink-900 placeholder:text-ink-900/35 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
              />
            </div>

            {/* Contact */}
            <div className="rounded-2xl border border-brand-100 bg-white p-6">
              <h2 className="mb-1 text-[13px] font-semibold uppercase tracking-wider text-ink-900/50">
                Telefoonnummer
              </h2>
              <p className="mb-4 text-[12px] text-ink-900/55">
                Optioneel. Collega&apos;s bereiken je standaard via een bericht binnen het platform;
                een telefoonnummer delen is niet verplicht.
              </p>
              <input
                type="tel"
                value={telefoon}
                onChange={(e) => setTelefoon(e.target.value)}
                placeholder="bijv. 06 12345678"
                className="w-full max-w-xs rounded-lg border border-brand-100 bg-brand-50/50 px-3 py-2 text-[13px] text-ink-900 placeholder:text-ink-900/35 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
              />
            </div>

            {/* IBAN + woonplaats voor kostendeclaraties (besluit Jos 20 aug / 26 okt 2026) */}
            <div className="rounded-2xl border border-brand-100 bg-white p-6">
              <h2 className="mb-1 text-[13px] font-semibold uppercase tracking-wider text-ink-900/50">
                Gegevens voor declaraties
              </h2>
              <p className="mb-4 text-[12px] text-ink-900/55">
                Optioneel. Vul je dit in, dan staan je IBAN en woonplaats voortaan klaar op het
                declaratieformulier. Ze zijn alleen zichtbaar voor jezelf, je teamleider en de
                administratie. Je woonplaats kun je desgewenst ook in het Smoelenboek tonen.
              </p>
              <div className="grid max-w-lg grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="profiel-iban"
                    className="mb-1 block text-[12px] font-medium text-ink-900/70"
                  >
                    IBAN
                  </label>
                  <input
                    id="profiel-iban"
                    type="text"
                    value={rekeningnummer}
                    onChange={(e) => setRekeningnummer(e.target.value)}
                    placeholder="NL91 ABNA 0417 1643 00"
                    className="w-full rounded-lg border border-brand-100 bg-brand-50/50 px-3 py-2 text-[13px] text-ink-900 placeholder:text-ink-900/35 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
                  />
                </div>
                <div>
                  <label
                    htmlFor="profiel-woonplaats"
                    className="mb-1 block text-[12px] font-medium text-ink-900/70"
                  >
                    Woonplaats
                  </label>
                  <input
                    id="profiel-woonplaats"
                    type="text"
                    value={woonplaats}
                    onChange={(e) => setWoonplaats(e.target.value)}
                    placeholder="bijv. Utrecht"
                    autoComplete="address-level2"
                    className="w-full rounded-lg border border-brand-100 bg-brand-50/50 px-3 py-2 text-[13px] text-ink-900 placeholder:text-ink-900/35 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
                  />
                  <label className="mt-2 flex items-center gap-2 text-[12px] text-ink-900/70">
                    <input
                      type="checkbox"
                      checked={woonplaatsZichtbaar}
                      onChange={(e) => setWoonplaatsZichtbaar(e.target.checked)}
                      className="h-4 w-4 rounded border-brand-200 text-brand-600 focus:ring-brand-400"
                    />
                    Toon in het Smoelenboek
                  </label>
                </div>
              </div>
            </div>

            {/* Lidnummers voor accreditatie */}
            {!isKantoor && (
              <div className="rounded-2xl border border-brand-100 bg-white p-6">
                <h2 className="mb-1 text-[13px] font-semibold uppercase tracking-wider text-ink-900/50">
                  Lidnummers
                </h2>
                <p className="mb-4 text-[12px] text-ink-900/55">
                  Optioneel. Vul je deze in, dan gaan ze automatisch mee bij elke aanmelding voor
                  scholing, zodat kantoor je accreditatiepunten kan bijschrijven zonder ze telkens
                  opnieuw te vragen.
                </p>
                <div className="grid max-w-lg grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-[12px] font-medium text-ink-900/70">
                      Lidnummer LVPOH-GGZ
                    </label>
                    <input
                      type="text"
                      value={lidnummerLvpoh}
                      onChange={(e) => setLidnummerLvpoh(e.target.value)}
                      maxLength={50}
                      className="w-full rounded-lg border border-brand-100 bg-brand-50/50 px-3 py-2 text-[13px] text-ink-900 placeholder:text-ink-900/35 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[12px] font-medium text-ink-900/70">
                      Lidnummer SKJ
                    </label>
                    <input
                      type="text"
                      value={lidnummerSkj}
                      onChange={(e) => setLidnummerSkj(e.target.value)}
                      maxLength={50}
                      className="w-full rounded-lg border border-brand-100 bg-brand-50/50 px-3 py-2 text-[13px] text-ink-900 placeholder:text-ink-900/35 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Teamleider (zelfde endpoint als op de declaratiepagina); rendert
                niets zolang de declaratiemodule uit staat. */}
            <TeamleiderKiezer />

            {/* Meldingen */}
            <div className="rounded-2xl border border-brand-100 bg-white p-6">
              <h2 className="mb-1 text-[13px] font-semibold uppercase tracking-wider text-ink-900/50">
                Meldingen
              </h2>
              <p className="mb-4 text-[12px] text-ink-900/55">
                Waarvoor je een meldingsstrook in beeld krijgt en een teller in de tabtitel. De bel
                rechtsboven toont altijd alles.
              </p>
              <div className="space-y-2">
                {(
                  [
                    ['dm', 'Directe berichten'],
                    ['kanaal', 'Kanaalberichten (groepsgesprekken)'],
                    ['overig', 'Overige meldingen (incidenten, declaraties, aanmeldingen)'],
                  ] as const
                ).map(([key, label]) => (
                  <label key={key} className="flex items-center gap-2.5 text-[13px] text-ink-900">
                    <input
                      type="checkbox"
                      checked={meldingen[key]}
                      onChange={(e) => setMeldingen({ ...meldingen, [key]: e.target.checked })}
                      className="h-4 w-4 rounded border-brand-200 text-brand-600 focus:ring-brand-400"
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3">
              {saved && (
                <p className="text-[13px] font-medium text-brand-700">Profiel opgeslagen.</p>
              )}
              {saveError && <p className="text-[13px] text-error">{saveError}</p>}
              <button
                type="button"
                onClick={() => void handleSave()}
                disabled={saving}
                className="rounded-xl bg-brand-600 px-5 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-brand-700 disabled:opacity-50"
              >
                {saving ? 'Opslaan…' : 'Opslaan'}
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
